import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  signal,
  computed,
  inject,
  DestroyRef,
  ChangeDetectorRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MenuItem, MessageService } from 'primeng/api';
import { Menu, MenuModule } from 'primeng/menu';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { PermissionService } from '@app/core/services/permission.service';

import {
  SearchFormComponent,
  FormFieldConfig,
  FILTER_ACTIVE_PARAM,
} from '@shared/components/search-form/search-form.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import type { TableColumn } from '@shared/components/data-table/data-table.component';
import { StatusFilterComponent } from '@shared/components/status-filter/status-filter.component';
import {
  ConfirmationDialogComponent,
  ConfirmationButton,
} from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import { ClassifySidebarComponent } from '../classify-sidebar/classify-sidebar.component';
import {
  ClassifyModel,
  CLASSIFY_TYPE_OPTIONS,
  GOODS_CLASSIFY_TYPES,
} from '../../models/classify.model';
import { getCategoryDisplayName } from '@shared/utils/treeselect.util';
import { ClassifyService } from '../../services/classify.service';
import {
  getCategorySearchConfig,
  CATEGORY_TABLE_COLUMNS,
  CLASSIFY_DEFAULT_COLUMNS_ORDER,
  CLASSIFY_FLEXIBLE_COLUMNS_ORDER,
  CLASSIFY_ACTION_COLUMN,
  CATEGORY_BUTTON_CONFIG,
  CATEGORY_SECONDARY_BUTTON_CONFIG,
} from '../../models/classify.config';

@Component({
  selector: 'app-classify',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    ConfirmDialogModule,
    MenuModule,
    ToastModule,
    TranslateModule,
    SearchFormComponent,
    AdvancedDataTableComponent,
    AppButtonComponent,
    StatusFilterComponent,
    ConfirmationDialogComponent,
    ClassifySidebarComponent,
  ],
  providers: [MessageService],
  templateUrl: './classify-list.component.html',
  styleUrls: ['./classify-list.component.css'],
  changeDetection: ChangeDetectionStrategy.Default, // Changed from OnPush to Default for better signal detection
})
export class ClassifyListComponent implements OnInit {
  private readonly classifyService = inject(ClassifyService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly messageService = inject(MessageService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  constructor(private readonly translateService: TranslateService) {}

  // Configuration
  searchConfig: FormFieldConfig[] = [];

  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );

  // Permission flags - computed from PermissionService
  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('product.category.view');
  });
  canCreate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('product.category.create');
  });
  canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('product.category.edit');
  });
  canDelete = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('product.category.delete');
  });
  // Check if user has any action permissions (UPDATE or DELETE)
  hasAnyActionPermission = computed(
    () => this.canUpdate() || this.canDelete(),
  );

  // Advanced table column configuration
  defaultColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      CATEGORY_TABLE_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];
    for (const key of CLASSIFY_DEFAULT_COLUMNS_ORDER) {
      const col = fromConfig(key);
      if (col) cols.push(col);
    }

    // Conditionally add actions column at the end if permitted
    if (this.hasAnyActionPermission() || this.canRead()) {
      const actionsCol = fromConfig(CLASSIFY_ACTION_COLUMN.ACTIONS);
      if (actionsCol) cols.push(actionsCol);
    }

    return cols;
  });

  flexibleColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      CATEGORY_TABLE_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];

    for (const prop of CLASSIFY_FLEXIBLE_COLUMNS_ORDER) {
      const col = fromConfig(prop);
      if (col) {
        cols.push(col);
      }
    }

    return cols;
  });

  // Button configuration base
  private readonly primaryButtonBase = CATEGORY_BUTTON_CONFIG;
  private readonly secondaryButtonBase = CATEGORY_SECONDARY_BUTTON_CONFIG;

  // Computed properties for button state
  hasEnabledCategories = computed(() =>
    this.selectedCategories().some((cat) => cat.statusFlag),
  );

  // Computed button configurations with permissions
  primaryButton = computed(() => ({
    ...this.primaryButtonBase,
    show: this.canCreate(),
  }));

  secondaryButton = computed(() => {
    const selected = this.selectedCategories();
    const hasEnabled = this.hasEnabledCategories();
    const hasOpenDraft = selected.some((category) => category.hasOpenDraft);
    const shouldShow = this.canDelete() && selected.length > 0 && !hasEnabled;
    return {
      ...this.secondaryButtonBase,
      show: shouldShow,
      disabled: !shouldShow || hasOpenDraft,
    };
  });

  // Data signals
  categories = signal<ClassifyModel[]>([]);
  loading = signal(false);
  totalRecords = signal(0);
  selectedCategories = signal<ClassifyModel[]>([]);
  categoriesTree = signal<ClassifyModel[]>([]);
  selectedCategory = signal<ClassifyModel | null>(null);

  // Search parameters
  searchParams = signal({
    code: '',
    name: '',
    subFlag: '',
    parentCategory: '', // Parent category dropdown selection
    selectedTreeItem: '', // Selected tree item from sidebar
    statusFlag: '',
    page: 0, // Default page should be 0
    size: 20,
  });

  // Confirmation dialog properties
  showConfirmationDialog = false;
  selectedCategoryForAction: ClassifyModel | null = null;
  dialogTitle = '';
  dialogMessage = '';
  dialogButtons: ConfirmationButton[] = [];
  currentAction: 'delete' | 'batchDelete' | 'statusChange' | null = null;

  // Loading states for different operations
  deleteLoading = signal(false);
  statusUpdateLoading = signal(false);
  currentMenuModel = signal<MenuItem[]>([]);

  // Signal to clear sidebar selection
  clearSidebarSelection = signal(false);

  // Flag to track if we're in tree selection mode
  isTreeSelectionMode = signal(false);

  ngOnInit(): void {
    // Initialize search config with translated options
    this.searchConfig = getCategorySearchConfig(this.translateService);

    this.loadCategoryTree();
    // Load all categories initially when no tree item is selected
    const urlParams = this.route.snapshot.queryParams;
    const hasSearchParams = urlParams[FILTER_ACTIVE_PARAM] === 'true';

    if (!hasSearchParams) {
      this.loadCategories();
    }

    // Subscribe to permissions loaded state to trigger change detection
    // This ensures computed signals re-evaluate when permissions are loaded
    this.permissionService.permissionsLoaded$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((loaded) => {
        if (loaded) {
          // Trigger change detection to update computed signals
          this.cdr.detectChanges();
        }
      });
  }

  /**
   * Load category tree on first page load
   */
  private loadCategoryTree(): void {
    this.loading.set(true);

    this.classifyService
      .getCategoryTree({
        statusFlag: undefined,
        subFlag: false,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (categories) => {
          this.categoriesTree.set(categories);
          this.loadTreeOptions(categories);
          this.updateSearchConfigWithParentCategories(categories);
          this.loading.set(false);
        },
        error: (error) => {
          console.error('Error loading category tree:', error);
          this.loading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant(
              'goods.classify.messages.error.title',
            ),
            detail: this.translateService.instant(
              'goods.classify.loading.failed_to_load_tree',
            ),
          });
        },
      });
  }

  /**
   * Update search config with parent categories tree options
   */
  private updateSearchConfigWithParentCategories(
    categories: ClassifyModel[],
  ): void {
    const treeOptions = this.convertToTreeNodes(categories);

    // Update the search config
    this.searchConfig = this.searchConfig.map((field) => {
      if (field.key === 'parentId') {
        return {
          ...field,
          treeOptions: treeOptions,
        };
      }
      return field;
    });
  }

  /**
   * Handle category selection from sidebar tree
   * Prevents duplicate API calls when search form is active
   */
  onCategorySelect(category: ClassifyModel | null): void {
    // Don't update if we're currently clearing selection
    if (this.clearSidebarSelection()) {
      return;
    }

    this.selectedCategory.set(category);

    if (category) {
      // Only submit the ID to the backend for tree item selection
      this.filterByTreeItemId(category.id);
    } else {
      this.searchParams.set({
        ...this.searchParams(),
        selectedTreeItem: '', // Clear selected tree item
        page: 0, // Reset to page 0
      });
      this.loadCategories();
    }
  }

  /**
   * Filter by tree item ID only - ensures only ID is submitted to BE for tree item selection
   * Preserves search form values to prevent form reset
   */
  private filterByTreeItemId(treeItemId: number | undefined): void {
    if (!treeItemId) {
      console.warn('No tree item ID provided for filtering');
      return;
    }

    // Validate that treeItemId is a positive number
    if (typeof treeItemId !== 'number' || treeItemId <= 0) {
      console.error(
        'Invalid tree item ID:',
        treeItemId,
        'Type:',
        typeof treeItemId,
      );
      return;
    }

    // Set tree selection mode flag
    this.isTreeSelectionMode.set(true);

    // Preserve existing search form values, only update the tree selection
    this.searchParams.set({
      ...this.searchParams(),
      selectedTreeItem: treeItemId.toString(), // Set selected tree item ID
      page: 0, // Reset to page 0
      // Keep other search form values (code, name, subFlag, parentCategory)
    });

    this.loadCategories();
  }

  private loadTreeOptions(categories: ClassifyModel[]): void {
    const treeOptions = this.convertToTreeNodes(categories);

    this.searchConfig = this.searchConfig.map((field) => {
      if (field.key === 'parentId') {
        return { ...field, treeOptions: treeOptions };
      }
      return field;
    });
  }

  private convertToTreeNodes(categories: ClassifyModel[]): any[] {
    // Use the same logic as sidebar - handle both hierarchical and flat structures
    if (!categories || categories.length === 0) {
      return [];
    }

    // Check if this is a hierarchical structure (from getTree API)
    if (this.isHierarchicalStructure(categories)) {
      return this.convertHierarchicalToTreeNodes(categories);
    }

    // Fall back to flat structure conversion (from getList2 API)
    return this.convertFlatToTreeNodes(categories);
  }

  /**
   * Check if the categories array has a hierarchical structure (same as sidebar)
   */
  private isHierarchicalStructure(categories: any[]): boolean {
    if (categories.length === 0) return false;
    const firstCategory = categories[0];
    return firstCategory?.children !== undefined;
  }

  /**
   * Convert hierarchical structure to tree nodes (same as sidebar)
   */
  private convertHierarchicalToTreeNodes(categories: any[]): any[] {
    const convertNode = (category: any): any => {
      return {
        label: getCategoryDisplayName(category),
        key: category.id?.toString() || '',
        children: category.children
          ? category.children.map((child: any) => convertNode(child))
          : [],
      };
    };

    return categories.map(convertNode);
  }

  /**
   * Convert flat structure to tree nodes (same as sidebar)
   */
  private convertFlatToTreeNodes(categories: ClassifyModel[]): any[] {
    // Create a map for quick lookup
    const categoryMap = new Map<number, any>();
    const rootNodes: any[] = [];

    // First pass: create all nodes
    categories.forEach((category) => {
      const node = {
        label: getCategoryDisplayName(category),
        key: category.id?.toString() || '',
        children: [],
      };
      categoryMap.set(category.id || 0, node);
    });

    // Second pass: build parent-child relationships
    categories.forEach((category) => {
      const node = categoryMap.get(category.id || 0);
      if (node) {
        if (!category.parentId || category.parentId === 0) {
          // Root node
          rootNodes.push(node);
        } else {
          // Child node
          const parentNode = categoryMap.get(category.parentId);
          if (parentNode) {
            if (!parentNode.children) {
              parentNode.children = [];
            }
            parentNode.children.push(node);
          } else {
            // Parent not found, treat as root
            rootNodes.push(node);
          }
        }
      }
    });

    // Update labels (no icons needed)
    const updateLabels = (nodes: any[]): void => {
      nodes.forEach((node) => {
        if (node.children) {
          updateLabels(node.children);
        }
      });
    };

    updateLabels(rootNodes);
    return rootNodes;
  }

  // Main table methods
  onSearch(params: any): void {
    // Clear tree selection mode flag
    this.isTreeSelectionMode.set(false);

    // Clear sidebar selection when search form is used
    this.clearSidebarSelection.set(true);

    this.searchParams.set({
      ...this.searchParams(),
      code: params.code || '',
      name: params.name || '',
      subFlag: params.subFlag,
      statusFlag: params.statusFlag,
      parentCategory: params.parentId || '',
      selectedTreeItem: '', // Clear selected tree item when searching
      page: 0,
    });

    this.loadCategories();

    // Reset the clear signal after a short delay
    setTimeout(() => {
      this.clearSidebarSelection.set(false);
    }, 100);
  }

  onPageChange(event: any): void {
    this.searchParams.set({
      ...this.searchParams(),
      page: event.page, // Use 0-based pagination (no +1)
      size: event.rows,
    });
    this.loadCategories();
  }

  /**
   * Reset search form and data table to default state
   */
  onReset(): void {
    // Clear sidebar selection
    this.clearSidebarSelection.set(true);

    // Clear tree selection mode flag
    this.isTreeSelectionMode.set(false);

    // Reset search parameters to default values
    this.searchParams.set({
      code: '',
      name: '',
      subFlag: '',
      statusFlag: '',
      parentCategory: '', // Clear parent category selection
      selectedTreeItem: '', // Clear tree item selection
      page: 0,
      size: 20,
    });

    // Clear selected categories
    this.selectedCategories.set([]);
    // secondaryButton is a computed signal, so it will update automatically

    // Load all categories (default state)
    this.loadCategories();

    // Show success message
    this.messageService.add({
      severity: 'info',
      summary: this.translateService.instant('common.messages.success'),
      detail: this.translateService.instant('goods.classify.reset.message'),
    });

    // Reset the clear signal after a short delay
    setTimeout(() => {
      this.clearSidebarSelection.set(false);
    }, 100);
  }

  onSelectionChange(selection: ClassifyModel[]): void {
    this.selectedCategories.set(selection);
    // secondaryButton is a computed signal, so it will update automatically
  }

  onEdit(category: ClassifyModel): void {
    if (this.blockOpenDraftMutation(category)) return;
    this.router.navigate(['/goods/classify/edit', category.id], {
      queryParams: this.route.snapshot.queryParams,
    });
  }

  onView(category: ClassifyModel): void {
    this.router.navigate(['/goods/classify/view', category.id], {
      queryParams: this.route.snapshot.queryParams,
    });
  }

  hasOthersActions(category: ClassifyModel): boolean {
    return this.canUpdate() || this.canDelete() || (this.canRead() && !!category.hasOpenDraft);
  }

  toggleOthersMenu(event: Event, category: ClassifyModel): void {
    event.preventDefault();
    event.stopPropagation();
    const busy = this.deleteLoading() || this.statusUpdateLoading();
    const items: MenuItem[] = [];

    if (this.canUpdate()) {
      items.push({
        label: this.translateService.instant(
          category.statusFlag
            ? 'goods.classify.actions.deactivate'
            : 'goods.classify.actions.activate',
        ),
        disabled: busy || !!category.hasOpenDraft,
        command: () => this.onStatusChange(category, !category.statusFlag),
      });
    }

    if (this.canDelete()) {
      items.push({
        label: this.translateService.instant('common.actions.delete'),
        disabled: busy || !!category.statusFlag || !!category.hasOpenDraft,
        command: () => this.onDelete(category),
      });
    }

    if (this.canRead()) {
      items.push({
        label: this.translateService.instant(
          'goods.classify.actions.open_draft',
        ),
        disabled: busy || !category.hasOpenDraft,
        command: () => this.onOpenDraft(category),
      });
    }

    this.currentMenuModel.set(items);
    this.actionMenu?.toggle(event);
  }

  onOpenDraft(category: ClassifyModel): void {
    if (!category.id || !category.hasOpenDraft) return;

    this.classifyService
      .getOpenDraftForCategory(category.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (draft) => {
          if (draft?.id) {
            this.router.navigate(['/goods/classify/drafts', draft.id]);
            return;
          }
          this.showOpenDraftError();
        },
        error: () => this.showOpenDraftError(),
      });
  }

  private showOpenDraftError(): void {
    this.messageService.add({
      severity: 'error',
      summary: this.translateService.instant(
        'goods.classify.messages.error.title',
      ),
      detail: this.translateService.instant(
        'goods.classify.messages.error.open_draft_failed',
      ),
    });
  }

  onAdd(): void {
    this.router.navigate(['/goods/classify/add']);
  }

  onDelete(category: ClassifyModel): void {
    if (this.blockOpenDraftMutation(category)) return;
    this.selectedCategoryForAction = category;
    this.dialogTitle = this.translateService.instant(
      'goods.classify.dialog.confirm_delete',
    );
    this.dialogMessage = this.translateService.instant(
      'goods.classify.dialog.confirm_delete_message',
      { name: category.name },
    );
    this.currentAction = 'delete';
    this.dialogButtons = [
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant('common.actions.delete'),
        action: 'confirm',
        severity: 'danger',
      },
    ];
    this.showConfirmationDialog = true;
  }

  onBatchDelete(): void {
    if (this.selectedCategories().length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'goods.classify.messages.select_categories_to_delete',
        ),
      });
      return;
    }

    if (this.selectedCategories().some((category) => category.hasOpenDraft)) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: 'One or more selected categories already have an open draft.',
      });
      return;
    }

    // Check if any enabled categories are selected
    const enabledCategories = this.selectedCategories().filter(
      (cat) => cat.statusFlag,
    );
    if (enabledCategories.length > 0) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'goods.classify.messages.cannot_delete_enabled_categories',
          {
            categories: enabledCategories.map((cat) => cat.name).join(', '),
          },
        ),
      });
      return;
    }

    this.dialogTitle = this.translateService.instant(
      'goods.classify.dialog.confirm_batch_delete',
    );
    this.dialogMessage = this.translateService.instant(
      'goods.classify.dialog.confirm_batch_delete_message',
      {
        count: this.selectedCategories().length,
      },
    );
    this.currentAction = 'batchDelete';
    this.dialogButtons = [
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant('common.actions.delete'),
        action: 'confirm',
        severity: 'danger',
      },
    ];
    this.showConfirmationDialog = true;
  }

  /**
   * Handle status change for a category
   */
  onStatusChange(category: ClassifyModel, newStatus: boolean): void {
    if (this.blockOpenDraftMutation(category)) return;
    const action = newStatus ? 'activate' : 'deactivate';
    this.selectedCategoryForAction = category;
    this.dialogTitle = this.translateService.instant(
      'goods.classify.dialog.confirm_status_change',
    );
    this.dialogMessage = this.translateService.instant(
      'goods.classify.dialog.confirm_status_change_message',
      {
        action: this.translateService.instant(`common.actions.${action}`),
        name: category.name,
      },
    );
    this.currentAction = 'statusChange';
    this.dialogButtons = [
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant(`common.actions.${action}`),
        action: 'confirm',
        severity: 'info',
      },
    ];
    this.showConfirmationDialog = true;
  }

  private blockOpenDraftMutation(category: ClassifyModel): boolean {
    if (!category.hasOpenDraft) return false;
    this.messageService.add({
      severity: 'warn',
      summary: this.translateService.instant('common.messages.warning'),
      detail: 'This category already has an open draft. Review it in the Category drafts tab.',
    });
    return true;
  }

  private loadCategories(): void {
    this.loading.set(true);
    const params = {
      categoryCode: this.searchParams().code || undefined,
      categoryName: this.searchParams().name || undefined,
      subFlag: this.searchParams().subFlag,
      statusFlag: this.searchParams().statusFlag,
      id: this.searchParams().selectedTreeItem
        ? Number(this.searchParams().selectedTreeItem)
        : undefined, // id parameter for selected tree item
      parentId: this.searchParams().parentCategory
        ? this.parseParentId(this.searchParams().parentCategory)
        : undefined, // parentId parameter for parent category dropdown
      page: this.searchParams().page,
      size: this.searchParams().size,
    };

    this.classifyService
      .getCategories(params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          // Validate the response data
          if (!response.data || !Array.isArray(response.data)) {
            console.error(
              'Component: Invalid response data - not an array:',
              response.data,
            );
            this.categories.set([]);
            this.totalRecords.set(0);
            this.loading.set(false);
            return;
          }

          // Update signals
          this.categories.set(response.data);
          this.totalRecords.set(response.totalRecords);
          this.loading.set(false);

          // Force change detection
          this.cdr.detectChanges();

          // Verify signal updates
          if (this.categories().length !== response.data.length) {
            console.error('Component: Signal update failed - length mismatch');
            console.error('Component: Expected length:', response.data.length);
            console.error(
              'Component: Actual signal length:',
              this.categories().length,
            );
          }

          if (this.totalRecords() !== response.totalRecords) {
            console.error(
              'Component: Signal update failed - total records mismatch',
            );
            console.error(
              'Component: Expected totalRecords:',
              response.totalRecords,
            );
            console.error(
              'Component: Actual signal totalRecords:',
              this.totalRecords(),
            );
          }
        },
        error: (error) => {
          console.error('Error loading categories:', error);
          this.loading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'goods.classify.messages.error.load_failed',
            ),
          });
        },
      });
  }

  /**
   * Parse parent ID value from various formats
   * Now handles the extracted key value from treeselect
   */
  private parseParentId(value: any): number | undefined {
    if (value === null || value === undefined || value === '') {
      return undefined;
    }

    // If it's already a number, return it
    if (typeof value === 'number') {
      return value;
    }

    // If it's a string, try to convert to number
    if (typeof value === 'string') {
      const numValue = Number(value);
      // Check if conversion was successful and it's a valid positive number
      if (!Number.isNaN(numValue) && numValue > 0) {
        return numValue;
      }
    }

    // If it's an object with key property (from treeselect), extract the key
    if (typeof value === 'object' && value.key) {
      const numValue = Number(value.key);
      if (!Number.isNaN(numValue) && numValue > 0) {
        return numValue;
      }
    }

    console.warn(
      'Component: Invalid parentId value:',
      value,
      'Type:',
      typeof value,
    );
    return undefined;
  }

  private deleteCategory(id: number): void {
    this.deleteLoading.set(true);

    this.classifyService
      .deleteCategory(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (success) => {
          if (success) {
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('common.messages.success'),
              detail: this.translateService.instant(
                'goods.classify.messages.success.category_deleted',
              ),
            });
            this.loadCategories();
            this.loadCategoryTree(); // Reload tree after deletion
          } else {
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('common.messages.error'),
              detail: this.translateService.instant(
                'goods.classify.messages.delete_failed',
              ),
            });
          }
          this.deleteLoading.set(false);
        },
        error: (error) => {
          console.error('Error deleting category:', error);
          this.deleteLoading.set(false);
          const errorInfo = this.classifyService.handleBackendValidationError(
            error,
            'delete',
          );
          this.messageService.add(errorInfo);
        },
      });
  }

  private updateCategoryStatus(id: number, status: boolean): void {
    this.statusUpdateLoading.set(true);

    this.classifyService
      .toggleCategoryStatus(id, status)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (success) => {
          if (success) {
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('common.messages.success'),
              detail: this.translateService.instant(
                status
                  ? 'goods.classify.messages.success.category_activated'
                  : 'goods.classify.messages.success.category_deactivated',
              ),
            });
            this.loadCategories();
            this.loadCategoryTree(); // Reload tree after status change
          } else {
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('common.messages.error'),
              detail: this.translateService.instant(
                'goods.classify.messages.status_update_failed',
              ),
            });
          }
          this.statusUpdateLoading.set(false);
        },
        error: (error) => {
          console.error('Error updating category status:', error);
          this.statusUpdateLoading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'goods.classify.messages.status_update_failed',
            ),
          });
        },
      });
  }

  // Status methods
  getStatusSeverity(status: boolean): 'success' | 'secondary' {
    if (status) {
      return 'success';
    }
    return 'secondary';
  }

  getStatusDisplayText(status: boolean): string {
    if (status) {
      return this.translateService.instant('common.table.status.active');
    }
    return this.translateService.instant('common.table.status.inactive');
  }

  getStatusIcon(status: boolean): string {
    if (status) {
      return 'pi pi-check-circle text-green-500';
    }
    return 'pi pi-times-circle text-gray-500';
  }

  getStatusStyle(status: boolean): any {
    if (status) {
      return {
        'background-color': '#ecfdf5',
        color: '#047857',
        border: 'none',
      };
    }
    return {
      'background-color': '#f9fafb',
      color: '#6b7280',
      border: 'none',
    };
  }

  // Category Type methods
  getCategoryTypeLabel(subFlag: boolean): string {
    const translationKey = GOODS_CLASSIFY_TYPES.get(subFlag);
    return translationKey
      ? this.translateService.instant(translationKey)
      : this.translateService.instant('goods.classify.unknown');
  }

  getCategoryTypeSeverity(subFlag: boolean): string {
    if (subFlag) {
      return 'info';
    }
    return 'secondary';
  }

  // Attribute Count methods
  getAttributeCount(attributeList: any[] | undefined): number {
    return attributeList?.length || 0;
  }

  // Type methods
  getTypeLabel(type: string): string {
    const typeOption = CLASSIFY_TYPE_OPTIONS.find(
      (option) => option.value === type,
    );
    return typeOption ? typeOption.label : type;
  }

  getTypeSeverity(type: string): string {
    switch (type) {
      case 'product':
        return 'info';
      case 'service':
        return 'warn';
      case 'digital':
        return 'success';
      case 'physical':
        return 'secondary';
      default:
        return 'info';
    }
  }

  // Confirmation dialog methods
  onConfirmatiButtonClick(action: string): void {
    if (action === 'cancel') {
      this.closeConfirmationDialog();
    } else if (action === 'confirm') {
      if (this.currentAction === 'delete') {
        this.confirmDelete();
      } else if (this.currentAction === 'batchDelete') {
        this.confirmBatchDelete();
      } else if (this.currentAction === 'statusChange') {
        this.confirmStatusChange();
      }
    }
  }

  confirmDelete(): void {
    if (this.selectedCategoryForAction?.id) {
      this.deleteCategory(this.selectedCategoryForAction.id);
    }
    this.closeConfirmationDialog();
  }

  confirmBatchDelete(): void {
    const ids = this.selectedCategories()
      .map((category) => category.id)
      .filter((id): id is number => id !== undefined);
    this.batchDeleteCategories(ids);
    this.closeConfirmationDialog();
  }

  confirmStatusChange(): void {
    if (this.selectedCategoryForAction?.id !== undefined) {
      const newStatus = !this.selectedCategoryForAction.statusFlag;
      this.updateCategoryStatus(this.selectedCategoryForAction.id, newStatus);
    }
    this.closeConfirmationDialog();
  }

  closeConfirmationDialog(): void {
    this.showConfirmationDialog = false;
    this.selectedCategoryForAction = null;
    this.currentAction = null;
    this.dialogTitle = '';
    this.dialogMessage = '';
    this.dialogButtons = [];
  }

  // Reference to advanced table component
  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;
  @ViewChild('actionMenu') actionMenu?: Menu;

  onColumnConfigClick(event: Event): void {
    const target = event.target as HTMLElement;
    // Find the actual button element from PrimeNG p-button
    // p-button wraps a button element, so we need to find it
    const pButtonElement = target.closest('p-button');
    const buttonElement = pButtonElement
      ? (pButtonElement.querySelector('button') as HTMLElement)
      : (target.closest('button') as HTMLElement) || target;

    if (buttonElement && this.advancedTable) {
      this.advancedTable.openColumnSelector(buttonElement);
    }
  }

  private batchDeleteCategories(ids: number[]): void {
    this.deleteLoading.set(true);

    this.classifyService
      .deleteCategories(ids)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (success) => {
          if (success) {
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('common.messages.success'),
              detail: this.translateService.instant(
                'goods.classify.messages.success.categories_deleted',
                { count: ids.length },
              ),
            });
            this.selectedCategories.set([]);
            this.loadCategories();
            this.loadCategoryTree(); // Reload tree after batch deletion
          } else {
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('common.messages.error'),
              detail: this.translateService.instant(
                'goods.classify.messages.error.batch_delete_failed',
              ),
            });
          }
          this.deleteLoading.set(false);
        },
        error: (error) => {
          console.error('Error in batch delete:', error);
          this.deleteLoading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'goods.classify.messages.error.batch_delete_partial_failed',
            ),
          });
        },
      });
  }
}
